import React, { useEffect, useRef, useState } from 'react';
import { useIsMutating } from 'react-query';
import { useDispatch } from 'react-redux';
import { Box, Checkbox, Text, VStack } from '@chakra-ui/react';
import PropTypes from 'prop-types';

import LoadingSpinner from '../../../../../../../../../../../../components/Spinner';
import { CypressIds } from '../../../../../../../../../../../../constants/cypressIds';
import { inAppText } from '../../../../../../../../../../../../constants/inAppText';
import { updateModalOptions } from '../../../../../../../../../../../../redux/modules/modal';
import recordKeys from '../../../../../../../../../../../../services/models/record/keys';
import { useAgentRemovalPreview } from '../../../../../../../../../../../../services/record-service';

const { removeAgentModal: removeAgentModalText } = inAppText.insuredProfile.policiesTable;

/**
 * RemoveAgentModal
 *
 * Body content for the "Remove Agent" confirmation modal (DEV-3074). Fetches a preview of the
 * removal so the copy can be tailored: it always states the agent is being removed from every
 * policy/all future correspondence on the record, and conditionally warns that a contact will be
 * replaced with the current user as a placeholder when the agent is the only insured/record contact.
 *
 * The confirm/cancel buttons live in the modal's footer (`options.buttons`, see PolicyNameGrid), so
 * this component renders the informational copy, the loading state, and the irreversibility
 * acknowledgement, and owns whether the footer's primary button is enabled.
 */
const RemoveAgentModal = ({ recordId, agentUserId }) => {
  const dispatch = useDispatch();

  // Removing the agent invalidates the record, and the preview key lives under it. Left enabled,
  // the preview would refetch mid-removal and legitimately 404 (the agent no longer has any
  // references on the record), flashing the error state over a request that actually succeeded.
  const isRemoving = useIsMutating(recordKeys.removeRecordAgentMutation(recordId)) > 0;

  // Latch it: the mutation settles slightly before the modal is dismissed, and re-enabling the
  // query in that gap would fire exactly the 404 we are avoiding. Once a removal starts, this
  // body never previews again -- it just shows the spinner until it unmounts.
  const removalStarted = useRef(false);
  if (isRemoving) removalStarted.current = true;
  const isDismissing = isRemoving || removalStarted.current;

  const {
    data: preview,
    isLoading,
    isError,
    error
  } = useAgentRemovalPreview(recordId, agentUserId, { enabled: !!recordId && !!agentUserId && !isDismissing });

  const hasPreview = !!preview?.agent;

  const [isAcknowledged, setIsAcknowledged] = useState(false);

  // Single source of truth for the footer button: it stays disabled until we have something real
  // to describe AND the user has acknowledged it.
  useEffect(() => {
    dispatch(updateModalOptions({ buttons: { primaryButtonOptions: { isDisabled: !hasPreview || !isAcknowledged } } }));
  }, [dispatch, hasPreview, isAcknowledged]);

  if (isDismissing) {
    return (
      <Box data-cy={CypressIds.removeAgentModal} h="150px">
        <LoadingSpinner isInline minHeight="150px" />
      </Box>
    );
  }

  if (isLoading) {
    return (
      <Box data-cy={CypressIds.removeAgentModal} h="150px">
        <LoadingSpinner isInline minHeight="150px" />
      </Box>
    );
  }

  if (isError || !hasPreview) {
    return (
      <Box data-cy={CypressIds.removeAgentModal}>
        <Text>
          {error?.status === 403
            ? 'You do not have permission to remove agents from this record.'
            : "Something went wrong loading this agent's information. Please close this window and try again."}
        </Text>
      </Box>
    );
  }

  const agentName = [preview.agent.first, preview.agent.last]
    .map(part => (part ?? '').trim())
    .filter(Boolean)
    .join(' ');
  const agentEmail = (preview.agent.email ?? '').trim();
  const willReplaceContact = !!preview.willReplaceInsuredContact;

  return (
    <VStack align="flex-start" data-cy={CypressIds.removeAgentModal} spacing={4}>
      <Text>{removeAgentModalText.getRemovalDescription(agentName, agentEmail)}</Text>
      {willReplaceContact && (
        <Text textStyle="bodyMd500">
          {removeAgentModalText.getContactReplacementDescription(preview.replacementContact?.name)}
        </Text>
      )}
      <Checkbox
        alignSelf="center"
        isChecked={isAcknowledged}
        onChange={event => setIsAcknowledged(event.target.checked)}
      >
        <Text
          data-cy={CypressIds.understandIrreversibleCheckbox}
          textStyle="bodyMd500"
        >
          {removeAgentModalText.acknowledgementLabel}
        </Text>
      </Checkbox>
    </VStack>
  );
};

RemoveAgentModal.propTypes = {
  recordId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  agentUserId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired
};

export default RemoveAgentModal;
